import { PaperContainer } from '@ui-components/containers/PaperContainer'
import { TightSectionContent } from '@ui-components/containers/TightSectionContent'
import { FixedButtonSection } from '@ui-components/global/LayoutStyling'
import { BodyText } from '@ui-components/typography/Typography'
import { JSX, ReactNode, useLayoutEffect, useRef } from 'react'
import { styled } from 'styled-components'

import { DynamicWidthButtonProps, PrimaryButton, SecondaryButton } from '../actions/special/DynamicWidthButtons'
import { BlueInfoIcon } from '../assets/Icons'
import { Divider } from '../decorative/Divider'
import { Colors } from '../global/colors'
import { ResponsiveButtonContainer } from '../global/CommonStyling'
import {
  BREAKPOINT_DESKTOP_PX,
  BREAKPOINT_LARGE_DESKTOP_PX,
  LARGE_CONTAINER_MAX_WIDTH_PX,
  PAGE_COMPONENT_GAP,
  SECTION_HORIZONTAL_PADDING_DEFAULT,
  SECTION_VERTICAL_PADDING_DEFAULT,
} from '../global/constants'
import { LEGACY_SPACE_XLARGE } from '../global/legacy/spacing'
import { SPACE_LARGE_PLUS, SPACE_SMALL, SPACE_XXXXLARGE } from '../global/spacing'
import { ButtonContainer } from '../layout/ButtonContainer'
import { ConfirmButtonProps, WizardStepContentProps } from '../layout/WizardTypes'
import { FloatingBarWrapper, FloatingTopBar } from '../navigation/FloatingTopBar/FloatingTopBar'
import { QuaternaryHeading } from '../typography/headings/QuaternaryHeading'
import { IntroSection } from './IntroSection'
import { LargeScreenContainer } from './LargeScreenContainer'

const FixedConfirmSection = styled(FixedButtonSection)`
  transition:
    transform 200ms,
    opacity 200ms;
  overflow: hidden;
  box-shadow: 0px 4px 8px 2px rgb(0 0 0 / 10%);
  height: auto;
  width: 100%;
  left: 0;

  &.visible {
    transform: translateY(0);
    opacity: 1;
  }

  &.hidden {
    transform: translateY(100%);
    opacity: 0;
  }
`

const FloatingButtonContainer = styled.div`
  max-width: ${LARGE_CONTAINER_MAX_WIDTH_PX}px;
  margin: 0 auto;
  padding: ${SPACE_SMALL};
`

const CtaHeading = styled.div`
  margin-bottom: ${SPACE_SMALL};
`

const CheckInformationSection = styled.div`
  margin-top: -16px;
  background: ${Colors.BackgroundInfo};
  border-top: 1px solid ${Colors.BorderWeakNeutral};
  color: ${Colors.TextInfo};
  padding: 16px;
  display: flex;
  justify-content: flex-start;
  text-align: left;

  & > *:first-child {
    margin-right: 10px;
    flex-shrink: 0;
  }

  p:not(:last-child) {
    margin-bottom: 16px;
  }
`

const FooterContainer = styled.div`
  margin-top: ${SPACE_LARGE_PLUS};
`

const ConfirmSection = styled(TightSectionContent)`
  margin-top: ${PAGE_COMPONENT_GAP - SECTION_VERTICAL_PADDING_DEFAULT}px;
  padding: 0 ${SPACE_SMALL} ${LEGACY_SPACE_XLARGE};

  @media (min-width: ${BREAKPOINT_DESKTOP_PX}px) {
    padding: 0 ${SECTION_HORIZONTAL_PADDING_DEFAULT}px ${LEGACY_SPACE_XLARGE};
  }

  @media (min-width: ${BREAKPOINT_LARGE_DESKTOP_PX}px) {
    padding: 0 ${SPACE_XXXXLARGE} ${LEGACY_SPACE_XLARGE};
  }
`

type DisplayedButtonKind = 'primary' | 'secondary'

interface DisplayedButtonProps {
  kind?: DisplayedButtonKind
  buttonProps?: ConfirmButtonProps
  isLoading?: boolean
  disabled?: boolean
  buttonRef?: React.Ref<HTMLButtonElement>
  shouldUseButtonContainer?: boolean
  ['aria-disabled']?: boolean
  ['data-testid']: string
}
const DisplayedButton = ({
  kind = 'primary',
  buttonProps,
  isLoading,
  disabled,
  buttonRef,
  shouldUseButtonContainer = true,
  'aria-disabled': ariaDisabled,
  'data-testid': dataTestId,
}: DisplayedButtonProps) => {
  let button: ReactNode

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSubmit = (e: React.BaseSyntheticEvent<object, any, any>) => {
    if (e.currentTarget.ariaDisabled === 'true') {
      return
    }
    buttonProps?.onConfirm()
  }

  if (buttonProps) {
    const buttonLabel = buttonProps?.confirmLabel
    const testId = dataTestId + (isLoading ? '-loading' : '')
    const props: DynamicWidthButtonProps = {
      ref: buttonRef,
      label: buttonLabel,
      onClick: handleSubmit,
      disabled: !!buttonProps.disabled || !!disabled,
      loading: isLoading,
      'data-testid': testId,
      'aria-disabled': ariaDisabled,
    }
    if (kind === 'primary') {
      button = <PrimaryButton {...props} />
    } else {
      button = <SecondaryButton {...props} />
    }
  }
  if (shouldUseButtonContainer) {
    return <ButtonContainer>{button}</ButtonContainer>
  }
  return button
}

export const WizardPageContent = (props: WizardStepContentProps): JSX.Element | null => {
  const largeScreenContainerRef = useRef<HTMLDivElement | null>(null)
  const fixedConfirmSectionRef = useRef<HTMLDivElement | null>(null)

  const {
    children,
    confirmButtonProps,
    secondaryButtonProps,
    pageMessage,
    isLoading,
    customFooter,
    hideBoxShadow,
    subHeaderOptions,
    confirmButtonRef,
    ctaHeadingProps,
  } = props

  const shouldShowFixedButton = !!confirmButtonProps?.shouldShowConfirmFloating
  const shouldShowStandardButton = !!confirmButtonProps && !shouldShowFixedButton
  const shouldUseButtonContainer = !secondaryButtonProps
  const shouldRender = !!(children || pageMessage || confirmButtonProps)
  const fullWidthSupport = props.contentContainerType === 'wide'

  useLayoutEffect(() => {
    const updatePadding = () => {
      if (fixedConfirmSectionRef.current && largeScreenContainerRef.current) {
        const height = fixedConfirmSectionRef.current.getBoundingClientRect().height
        largeScreenContainerRef.current.style.paddingBottom = `${height}px`
      }
    }
    updatePadding()
    window.addEventListener('resize', updatePadding)
    return () => window.removeEventListener('resize', updatePadding)
  }, [])

  return shouldRender ? (
    <>
      <PaperContainer $hideBoxShadow={hideBoxShadow} $fullWidthSupport={fullWidthSupport} $largeScreenSupport={true}>
        {pageMessage && (
          <CheckInformationSection>
            <BlueInfoIcon />
            <BodyText>{pageMessage}</BodyText>
          </CheckInformationSection>
        )}
        {subHeaderOptions && !subHeaderOptions.isFixedSubHeader && (
          <FloatingBarWrapper>
            <FloatingTopBar
              secondaryTopBarProps={subHeaderOptions}
              progressBarProps={subHeaderOptions.showProgressBar ? subHeaderOptions.progressBarOptions : undefined}
            />
          </FloatingBarWrapper>
        )}
        <LargeScreenContainer $smallTopPadding={subHeaderOptions?.showProgressBar} ref={largeScreenContainerRef}>
          {props.pageIntro && (
            <IntroSection
              headingLevel={1}
              progressText={subHeaderOptions?.progressBarOptions?.['aria-label']}
              data-testid="intro-section"
              {...props.pageIntro}
            />
          )}
          {children}
        </LargeScreenContainer>
        <FixedConfirmSection
          className={shouldShowFixedButton ? 'visible' : 'hidden'}
          aria-hidden={!shouldShowFixedButton}
          data-testid="fixed-confirm-section"
          ref={fixedConfirmSectionRef}
        >
          <FloatingButtonContainer>
            {ctaHeadingProps && (
              <CtaHeading data-testid="cta-heading">
                <QuaternaryHeading header={ctaHeadingProps.header} additionalText={ctaHeadingProps.additionalText} />
              </CtaHeading>
            )}
            <ResponsiveButtonContainer>
              {secondaryButtonProps && (
                <DisplayedButton
                  kind="secondary"
                  disabled={!shouldShowFixedButton}
                  buttonProps={secondaryButtonProps}
                  isLoading={isLoading}
                  aria-disabled={isLoading}
                  shouldUseButtonContainer={shouldUseButtonContainer}
                  data-testid="confirm-page-button-fab-secondary"
                />
              )}
              <DisplayedButton
                disabled={!shouldShowFixedButton}
                buttonProps={confirmButtonProps}
                isLoading={isLoading}
                aria-disabled={isLoading}
                shouldUseButtonContainer={shouldUseButtonContainer}
                data-testid="confirm-page-button-fab"
              />
            </ResponsiveButtonContainer>
          </FloatingButtonContainer>
        </FixedConfirmSection>
        {shouldShowStandardButton && (
          <ConfirmSection>
            <Divider />
            <ResponsiveButtonContainer>
              {secondaryButtonProps && (
                <DisplayedButton
                  kind="secondary"
                  buttonProps={secondaryButtonProps}
                  isLoading={isLoading}
                  aria-disabled={isLoading}
                  shouldUseButtonContainer={shouldUseButtonContainer}
                  data-testid="confirm-page-button-secondary"
                />
              )}
              <DisplayedButton
                buttonProps={confirmButtonProps}
                isLoading={isLoading}
                aria-disabled={isLoading}
                buttonRef={confirmButtonRef}
                shouldUseButtonContainer={shouldUseButtonContainer}
                data-testid="confirm-page-button"
              />
            </ResponsiveButtonContainer>
          </ConfirmSection>
        )}
      </PaperContainer>
      {customFooter && <FooterContainer>{customFooter}</FooterContainer>}
    </>
  ) : null
}
